// TransactionPicker — search-as-you-type transaction selector for scenario adjustments.
// Owns data fetching for both revenue (Sales) and cost (Purchase) usages: in live
// mode it loads outstanding transactions via GraphQL with cursor-based infinite
// scroll and server-side search; in demo mode it filters a local array client-side.
// The only differences between the two usages are the `source` filter and the
// counterparty label.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown, Check, Receipt, FileText, CreditCard, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { AppMode } from '@/providers/ModeProvider';
import { getAllTransactionsGraphql } from '@/api/services/allTransactionGraphqlService';
import type { AllTransactionViewFilterInput, AllTransactionViewNode } from '@/api/models/allTransactionView';

export interface PickerTransaction {
  id: string;
  type: string;
  customer: string;
  description: string;
  amount: number;
  dueDate: string;
  status?: string;
  invoiceNumber?: string;
}

export type TransactionSource = 'Sales' | 'Purchase';

interface TransactionPickerProps {
  /** Which ledger to fetch. Drives the AllTransactionView `source` filter. */
  source: TransactionSource;
  /** Counterparty noun — "Customer" for revenue, "Supplier" for cost. */
  counterpartyLabel?: string;
  /** Demo vs live. In demo mode the picker never calls GraphQL. */
  mode: AppMode;
  /** Local transactions used for client-side filtering in demo mode. */
  demoTransactions: PickerTransaction[];
  value: string; // selected transaction id
  onChange: (id: string) => void;
  /** Notifies the parent of the full selected transaction (or null when cleared). */
  onSelect?: (txn: PickerTransaction | null) => void;
  /** Seeds the trigger label when editing before any list has loaded. */
  initialSelected?: PickerTransaction | null;
  /** When true the selection is locked (edit mode) — the trigger can't be opened or cleared. */
  disabled?: boolean;
  /** Transaction ids to hide from the list (e.g. already used by another adjustment). */
  excludeIds?: string[];
  placeholder?: string;
  className?: string;
}

const PAGE_SIZE = 100;

// AllTransactionView.Source literals live in db/Scripts/05_Views/vw_AllTransactions.sql:
// 'Sales Ledger'/'Sales Order' and 'Purchase Ledger'/'Purchase Order'. A beginsWith
// filter captures both ledger and order rows for the given side.
const SOURCE_FILTER_PREFIX: Record<TransactionSource, string> = {
  Sales: 'Sales',
  Purchase: 'Purchase',
};

const DEFAULT_TYPE: Record<TransactionSource, string> = {
  Sales: 'Invoice',
  Purchase: 'Supplier Bill',
};

/** Safely format a due date, returning null when missing/unparseable. */
function formatDueDate(dueDate: string): string | null {
  if (!dueDate) return null;
  const d = new Date(dueDate);
  return Number.isNaN(d.getTime()) ? null : format(d, 'dd MMM yyyy');
}

/** Trigger-label suffix: " · Due <date>" or empty when no valid date. */
function formatDueLabel(dueDate: string): string {
  const label = formatDueDate(dueDate);
  return label ? ` · Due ${label}` : '';
}

const TYPE_ICON: Record<string, React.ReactNode> = {
  'Invoice': <Receipt className="h-3.5 w-3.5" />,
  'Credit Note': <CreditCard className="h-3.5 w-3.5" />,
  'Cashbook': <FileText className="h-3.5 w-3.5" />,
};

const TYPE_COLOUR: Record<string, string> = {
  'Invoice': 'text-blue-600 bg-blue-50',
  'Credit Note': 'text-amber-600 bg-amber-50',
  'Cashbook': 'text-gray-600 bg-gray-50',
};

function StatusDot({ status }: { status?: string }) {
  if (!status) return null;
  const s = status.toLowerCase();
  const colour =
    s === 'overdue' ? 'bg-red-500' :
    s === 'due' ? 'bg-amber-500' :
    s === 'pending' ? 'bg-blue-500' :
    'bg-gray-400';
  return <span className={cn('inline-block h-2 w-2 rounded-full flex-shrink-0', colour)} title={status} />;
}

function TransactionRow({
  txn,
  isSelected,
  onSelect,
}: {
  txn: PickerTransaction;
  isSelected: boolean;
  onSelect: (txn: PickerTransaction) => void;
}) {
  const isNegative = txn.amount < 0;
  return (
    <button
      type="button"
      onClick={() => onSelect(txn)}
      className={cn(
        'w-full flex items-start gap-3 px-3 py-2.5 rounded-md text-left transition-colors',
        'hover:bg-accent focus:outline-none focus:bg-accent',
        isSelected && 'bg-primary/5 ring-1 ring-primary/20',
      )}
    >
      {/* Type badge */}
      <span className={cn(
        'flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 mt-0.5',
        TYPE_COLOUR[txn.type] ?? 'text-gray-600 bg-gray-50',
      )}>
        {TYPE_ICON[txn.type] ?? <FileText className="h-3.5 w-3.5" />}
        <span className="hidden sm:inline">{txn.type}</span>
      </span>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-sm truncate">{txn.customer}</span>
          {txn.invoiceNumber && (
            <span className="text-xs text-muted-foreground flex-shrink-0">#{txn.invoiceNumber}</span>
          )}
          <StatusDot status={txn.status} />
        </div>
        {txn.description && (
          <p className="text-xs text-muted-foreground truncate">{txn.description}</p>
        )}
        {formatDueDate(txn.dueDate) && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Due: {formatDueDate(txn.dueDate)}
          </p>
        )}
      </div>

      {/* Amount */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={cn(
          'text-sm font-semibold',
          isNegative ? 'text-red-600' : 'text-gray-900',
        )}>
          {isNegative ? '-' : ''}£{Math.abs(txn.amount).toLocaleString()}
        </span>
        {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
      </div>
    </button>
  );
}

export function TransactionPicker({
  source,
  counterpartyLabel = 'Customer',
  mode,
  demoTransactions,
  value,
  onChange,
  onSelect,
  initialSelected = null,
  disabled = false,
  excludeIds,
  placeholder,
  className,
}: TransactionPickerProps) {
  const isLive = mode === 'live';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedTxn, setSelectedTxn] = useState<PickerTransaction | null>(initialSelected);

  // Live-mode fetching state
  const [liveList, setLiveList] = useState<PickerTransaction[]>([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchingRef = useRef(false);
  const queryRef = useRef('');
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const resolvedPlaceholder =
    placeholder ?? `Search by ${counterpartyLabel.toLowerCase()}, reference or amount…`;

  const buildFilter = useCallback((search: string): AllTransactionViewFilterInput => {
    const filter: AllTransactionViewFilterInput = {
      outstanding: { ne: 0 },
      source: { beginsWith: SOURCE_FILTER_PREFIX[source] },
    };
    if (search.trim()) filter.summary = search.trim();
    return filter;
  }, [source]);

  const mapNode = useCallback((node: AllTransactionViewNode): PickerTransaction => ({
    id: String(node.id),
    type: node.transactionType || DEFAULT_TYPE[source],
    customer: node.accountName || 'Unknown',
    description: node.description || '',
    amount: node.amount ?? 0,
    dueDate: node.dueDate || node.anticipatedPaymentDate || node.transactionDate || '',
    status: node.status ?? undefined,
  }), [source]);

  const loadFirstPage = useCallback(async (search: string) => {
    if (!isLive) return;
    fetchingRef.current = true;
    setInitialLoading(true);
    try {
      const conn = await getAllTransactionsGraphql(
        buildFilter(search),
        PAGE_SIZE,
        undefined,
        [{ field: 'TransactionDate', order: 'Descending' }],
      );
      setLiveList(conn.edges.map((e) => mapNode(e.node)));
      setHasNextPage(conn.pageInfo.hasNextPage);
      setEndCursor(conn.pageInfo.endCursor);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setLiveList([]);
      setHasNextPage(false);
      setEndCursor(null);
    } finally {
      setInitialLoading(false);
      fetchingRef.current = false;
    }
  }, [isLive, buildFilter, mapNode]);

  const loadNextPage = useCallback(async () => {
    if (!isLive || fetchingRef.current || !hasNextPage || !endCursor) return;
    fetchingRef.current = true;
    setLoadingMore(true);
    try {
      const conn = await getAllTransactionsGraphql(
        buildFilter(queryRef.current),
        PAGE_SIZE,
        endCursor,
        [{ field: 'TransactionDate', order: 'Descending' }],
      );
      setLiveList((prev) => [...prev, ...conn.edges.map((e) => mapNode(e.node))]);
      setHasNextPage(conn.pageInfo.hasNextPage);
      setEndCursor(conn.pageInfo.endCursor);
    } catch (err) {
      console.error('Failed to fetch more transactions:', err);
    } finally {
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [isLive, hasNextPage, endCursor, buildFilter, mapNode]);

  // Hide transactions already used by another adjustment (never the current selection).
  const isExcluded = useCallback(
    (id: string) => !!excludeIds && id !== value && excludeIds.includes(id),
    [excludeIds, value],
  );

  // Demo-mode client-side filtering; live-mode list is already server-filtered.
  const filteredDemo = (query.trim()
    ? demoTransactions.filter((t) =>
        t.customer.toLowerCase().includes(query.toLowerCase()) ||
        t.description.toLowerCase().includes(query.toLowerCase()) ||
        t.type.toLowerCase().includes(query.toLowerCase()) ||
        (t.invoiceNumber?.toLowerCase().includes(query.toLowerCase()) ?? false) ||
        String(t.amount).includes(query),
      )
    : demoTransactions
  ).filter((t) => !isExcluded(t.id));

  const displayList = (isLive ? liveList : filteredDemo).filter((t) => !isExcluded(t.id));

  // Group by type for demo browse mode
  const grouped: Record<string, PickerTransaction[]> = {};
  for (const txn of filteredDemo) {
    if (!grouped[txn.type]) grouped[txn.type] = [];
    grouped[txn.type].push(txn);
  }

  const handleQueryChange = useCallback((q: string) => {
    setQuery(q);
    queryRef.current = q;
    if (!isLive) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setEndCursor(null);
      setHasNextPage(false);
      setLiveList([]);
      loadFirstPage(q);
    }, 300);
  }, [isLive, loadFirstPage]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    queryRef.current = '';
    if (isLive) loadFirstPage('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSelect = (txn: PickerTransaction) => {
    setSelectedTxn(txn);
    onChange(txn.id);
    onSelectRef.current?.(txn);
    setOpen(false);
    setQuery('');
    queryRef.current = '';
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTxn(null);
    onChange('');
    onSelectRef.current?.(null);
  };

  const handleScroll = () => {
    if (!isLive) return;
    const el = listRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadNextPage();
    }
  };

  // Resolve the selected transaction object from whichever list is loaded and
  // hand the fresh object up to the parent. Never emits a spurious null on mount
  // so a parent-seeded selection (edit mode) is preserved until a match loads.
  const currentList = isLive ? liveList : demoTransactions;
  useEffect(() => {
    if (!value) return;
    const found = currentList.find((t) => t.id === value);
    if (found && found !== selectedTxn) {
      setSelectedTxn(found);
      onSelectRef.current?.(found);
    }
  }, [value, currentList, selectedTxn]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Cleanup debounce
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        aria-disabled={disabled}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-input bg-background',
          'transition-colors text-left',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
          disabled ? 'cursor-not-allowed opacity-70' : 'hover:bg-accent',
          open && 'ring-2 ring-ring',
        )}
      >
        {selectedTxn ? (
          <>
            <span className={cn(
              'flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0',
              TYPE_COLOUR[selectedTxn.type] ?? 'text-gray-600 bg-gray-50',
            )}>
              {TYPE_ICON[selectedTxn.type] ?? <FileText className="h-3.5 w-3.5" />}
            </span>
            <span className="flex-1 truncate font-medium">{selectedTxn.customer}</span>
            <span className="text-xs text-muted-foreground truncate hidden sm:block">
              £{Math.abs(selectedTxn.amount).toLocaleString()}
              {formatDueLabel(selectedTxn.dueDate)}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="ml-1 p-0.5 rounded hover:bg-muted-foreground/20 text-muted-foreground flex-shrink-0"
                title="Clear selection"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </>
        ) : (
          <>
            <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground flex-1">{resolvedPlaceholder}</span>
            <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform flex-shrink-0', open && 'rotate-180')} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className={cn(
          'absolute z-50 mt-1 w-full min-w-[320px] rounded-lg border border-border bg-popover shadow-lg',
          'animate-in fade-in-0 zoom-in-95',
        )}>
          {/* Search input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                placeholder={resolvedPlaceholder}
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                className={cn(
                  'w-full pl-8 pr-8 py-1.5 text-sm rounded-md bg-muted/50',
                  'border border-transparent focus:border-input focus:bg-background',
                  'outline-none transition-colors placeholder:text-muted-foreground',
                )}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => handleQueryChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div ref={listRef} onScroll={handleScroll} className="overflow-y-auto max-h-72 py-1 px-1">
            {isLive && initialLoading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading transactions…
              </div>
            ) : displayList.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {query ? `No transactions match "${query}"` : 'No transactions available'}
              </div>
            ) : isLive ? (
              // Live mode — flat, server-sorted list with infinite scroll
              <>
                {displayList.map(txn => (
                  <TransactionRow
                    key={txn.id}
                    txn={txn}
                    isSelected={txn.id === value}
                    onSelect={handleSelect}
                  />
                ))}
                {loadingMore && (
                  <div className="flex items-center justify-center py-3 gap-2 text-muted-foreground text-xs">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading…
                  </div>
                )}
              </>
            ) : query.trim() ? (
              // Demo search results — flat list
              <>
                <p className="px-3 py-1 text-xs text-muted-foreground font-medium">
                  {filteredDemo.length} result{filteredDemo.length !== 1 ? 's' : ''}
                </p>
                {filteredDemo.map(txn => (
                  <TransactionRow
                    key={txn.id}
                    txn={txn}
                    isSelected={txn.id === value}
                    onSelect={handleSelect}
                  />
                ))}
              </>
            ) : (
              // Demo browse mode — grouped by type
              Object.entries(grouped).map(([type, txns]) => (
                <div key={type}>
                  <p className="px-3 py-1 text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    {type}s ({txns.length})
                  </p>
                  {txns.map(txn => (
                    <TransactionRow
                      key={txn.id}
                      txn={txn}
                      isSelected={txn.id === value}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
